import colors from 'lib/constants/resourceColors';
import Privileges from 'lib/constants/privileges';
import api from 'api';

// Contract status is derived server-side (view contracts_extended.status):
// draft (before start_date) / active / expired (>= expires) / cancelled (dead_date reached).
function statusBadgeStyle(value, data) {
    const status = (data && data.status) || value;

    switch (status) {
        case 'active':
            return {status: 'success', text: 'Active'};
        case 'draft':
            return {status: 'processing', text: 'Draft'};
        case 'expired':
            return {status: 'default', text: 'Expired'};
        case 'cancelled':
            return {status: 'error', text: 'Cancelled'};
        default:
            return {status: 'default', text: status || 'Unknown'};
    }
}

// Header form fields for the detail/edit + create screens.
export const form = [

    // --- Identity / lineage (read-only, trigger-stamped) ---
    {key: 'contract_number', label: 'Contract #', static: true, readOnly: true},
    {key: 'contract_number_base', label: 'Lineage Base', static: true, readOnly: true},
    {key: 'generation_seq', label: 'Term #', type: 'number', static: true, readOnly: true},
    {key: 'status', label: 'Status', type: 'badge', badgePropsFunc: statusBadgeStyle, static: true, readOnly: true},

    // --- Editable header ---
    // Sourced from the contract_types catalog (Administration > Contract Types),
    // via GET /v1/enums/contract_types (returns all rows, like /enums/tax_regions).
    // Value stored is contract_types.type; the label shows "type — description".
    // createOnly: the type is chosen once, on creation (via the contract-type picker
    // popup, see components/ContractCreate). It snapshots the type's defaults onto the
    // contract, so changing it afterward would silently desync the stored rates/discounts.
    // In create mode this renders as an editable dropdown; in the detail/edit view it
    // renders read-only (static), and normalizeValues strips it from PATCH bodies. The
    // server also rejects it on update (resources/contracts.js readOnly.update).
    {key: 'contract_type',
        label: 'Contract Type',
        type: 'options',
        createOnly: true,
        defaultValue: 'default',
        optionsURL: '/enums/contract_types',
        optionsMapper: (ct) => ({value: ct.type, label: ct.description ? `${ct.type} — ${ct.description}` : ct.type})},

    {key: 'site_id',
        displayKey: ['site_name'],
        label: 'Site',
        link: {url: '/sites/:site_id', privileges: Privileges.READ_SITES},
        type: 'siteAutocomplete',
        suggestFormat: (s) => {
            let text = `${s.name} (${s.address || 'No Address'})`;
            return {value: s.id, text};
        },
        suggestUrl: '/sites',
        suggestFields: ['id', 'name', 'address']},

    {key: 'bill_to_customer_id',
        displayKey: ['bill_to_customer_name'],
        label: 'Bill-To Customer',
        link: {url: '/customers/:bill_to_customer_id', privileges: Privileges.READ_CUSTOMERS},
        // Customer autocomplete + a "use site's bill-to customer" shortcut that copies
        // the selected site's default billing customer (sites.billing_customer_id) into
        // this field. Reads the live site_id via siteFieldKey (default 'site_id').
        type: 'siteBillToCustomer',
        suggestFormat: (c) => {
            let text = `${c.name} (${c.address || 'No Address'})`;
            return {value: c.id, text};
        },
        suggestUrl: '/customers',
        suggestFields: ['id', 'name', 'address']},

    // Dropdown populated from the defined tax_regions (GET /v1/enums/tax_regions).
    // Value stored is tax_regions.name; label shows tax_regions.display_name.
    // 'notax' ("No Tax") is a non-taxable-activity flag and is never valid on a
    // contract — filter it out here (contract page only). 'exempt' ("Tax Exempt")
    // stays, since that describes a tax-exempt customer/site and is reported separately.
    {key: 'tax_region',
        label: 'Tax Region',
        type: 'options',
        optionsURL: '/enums/tax_regions',
        optionsFilter: (tr) => tr.name !== 'notax',
        optionsMapper: (tr) => ({value: tr.name, label: `${tr.display_name}, ${tr.name}`})},

    // Manually-entered original agreement/inception date, independent of this term's
    // start_date. Nullable and never auto-filled (leave blank when unknown). Wired end-to-end
    // like start_date: editable `date` field that round-trips through contracts_extended
    // (read) and the base contracts column (write) -- on create via the createContract RPC,
    // on edit via the generic PATCH.
    {key: 'original_date', label: 'Original Date', type: 'date'},
    {key: 'start_date', label: 'Start Date', type: 'date'},
    {key: 'expires', label: 'Expires', type: 'date', static: true, readOnly: true},

    {key: 'term_months', label: 'Term (months)', type: 'number', defaultValue: 12},
    {key: 'renewal_term_months', label: 'Renewal Term (months)', type: 'number', defaultValue: 12},

    // When on, Generate Invoice combines every grouped_billing contract of the same bill-to
    // customer that has a billable period into ONE 'C' invoice -- one line item per contract,
    // each contract's ledger row referencing the shared invoice. Off = one invoice per contract.
    {key: 'grouped_billing', label: 'Grouped Billing', type: 'boolean', defaultValue: false},
    {key: 'auto_renew', label: 'Auto Renew', type: 'boolean', defaultValue: true},
    // Stored as a whole-percent number (e.g. 5 = 5%), matching contract_types.default_increase_pct
    // (authored as a `number` in contractTypes.js) and the renewal math in the backend
    // (procedures/contracts.js: prevAmount * (1 + pct/100)). NOT the fraction-based `percentage`
    // widget (which ×100 on display, so 5 would render as 500%).
    {key: 'auto_renew_increase_pct',
        label: 'Auto-Renew Increase %',
        type: 'number',
        writePrivileges: [Privileges.WRITE_CONTRACTS_AUTO_RENEW_INCREASE_PCT]},

    // One-shot override of the NEXT renewal's Contract Amount. When > 0, the next renewal bills
    // exactly this figure (the increase % is NOT applied on top), then this field resets to 0 on
    // the successor contract, so later renewals fall back to the increase % -- unless refilled.
    // 0 (default) means "use the increase % math." Guarded by the same privilege as the amount.
    {key: 'auto_renew_amount',
        label: 'Auto-Renew Amount',
        type: 'currency',
        readPrivileges: [Privileges.READ_CONTRACTS_CONTRACT_AMOUNT],
        writePrivileges: [Privileges.WRITE_CONTRACTS_CONTRACT_AMOUNT]},

    {key: 'contract_amount',
        label: 'Contract Amount',
        type: 'currency',
        readPrivileges: [Privileges.READ_CONTRACTS_CONTRACT_AMOUNT],
        writePrivileges: [Privileges.WRITE_CONTRACTS_CONTRACT_AMOUNT]},

    // Recurring billing cadence. Static enum (backend billing_cycle_type):
    // monthly / quarterly / semi_annual / annual. Value stored is the enum literal.
    // Billing periods are computed from start_date server-side (contract_billing_periods);
    // this field only picks the cadence. Editable on create and edit (a lineage-constant
    // that renewContract carries forward). Defaults to 'monthly' to match the DB default.
    {key: 'billing_cycle',
        label: 'Billing Cycle',
        type: 'options',
        defaultValue: 'monthly',
        options: [
            {value: 'monthly',     label: 'Monthly'},
            {value: 'quarterly',   label: 'Quarterly'},
            {value: 'semi_annual', label: 'Semi-Annual'},
            {value: 'annual',      label: 'Annual'},
        ]},

    // Whole-percent number (e.g. 10 = 10%), matching contract_types.default_labor_discount_pct
    // (also a `number`). See auto_renew_increase_pct above for why this is not `percentage`.
    {key: 'labor_discount_pct',
        label: 'Labor Discount %',
        type: 'number',
        writePrivileges: [Privileges.WRITE_CONTRACTS_LABOR_DISCOUNT_PCT]},

    // Human-readable contract label; used as the line-item description on generated
    // 'C' (contract) invoices, falling back to a "Contract <number> - <period>" label when blank.
    {key: 'description', label: 'Description', type: 'longtext'},

    // Customer purchase-order number; passed through to generated 'C' invoices (invoices.po_number).
    {key: 'po_number', label: 'PO Number', type: 'text'},

    {key: 'notes', label: 'Notes', type: 'longtext'},

    // Cancel writes dead_date via a formAction; shown read-only here.
    {key: 'dead_date', label: 'Cancelled On', type: 'date', static: true, readOnly: true},

    // --- Legacy link hooks (decision #86) ---
    {key: 'legacy_contract_number', label: 'Legacy Contract #'},
    {key: 'legacy_source_key', label: 'Legacy Source Key'},

    // --- Sub-tables ---
    // Generalized rates (labor classifications AND non-labor rates), keyed by rate_type_code
    // -> rate_types. Replaces the old labor-only sub-table; labor rows are simply the rows
    // whose rate type has category 'labor'. Existing inline-editing UX is preserved: click a
    // row to edit its amounts, with the rate type shown read-only as the row key (contract_id
    // + rate_type_code is unique, so changing it could collide -- to change the rate type,
    // delete the row and add a new one). The contracts_extended view emits these under `rates`.
    {key: 'rates',
        type: 'references',
        heading: 'Rates',
        createLabel: 'Add Rate',
        createPrivileges: [Privileges.CREATE_CONTRACT_RATES],
        readPrivileges: [Privileges.READ_CONTRACT_RATES],
        deletePrivileges: [Privileges.DELETE_CONTRACT_RATES],
        initialLoadFromValues: true,
        dataUrl: '/contracts/:contract_number',
        dataTransform: 'rates',
        createUrl: (values) => { return '/contract_rates' },
        deleteUrl: (values) => { return '/contract_rates/:id' },
        editUrl: '/contract_rates/:id',
        editPrivileges: [Privileges.UPDATE_CONTRACT_RATES],
        editFields: [
            {key: 'rate_type_name', label: 'Rate Type', static: true, readOnly: true},
            {key: 'regular_rate', label: 'Regular Rate', type: 'currency'},
            {key: 'ot_rate', label: 'OT Rate', type: 'currency'},
            {key: 'tm_regular_rate', label: 'T&M Regular Rate', type: 'currency'},
            {key: 'tm_ot_rate', label: 'T&M OT Rate', type: 'currency'},
        ],
        injectedValues: (values) => { return {contract_id: values.id} },
        formFields: [
            {key: 'rate_type_code',
                label: 'Rate Type',
                type: 'options',
                required: true,
                optionsURL: '/enums/rate_types',
                optionsMapper: (rt) => ({value: rt.code, label: rt.name ? `${rt.name} (${rt.code})` : rt.code})},
            {key: 'regular_rate', label: 'Regular Rate', type: 'currency'},
            {key: 'ot_rate', label: 'OT Rate', type: 'currency'},
            {key: 'tm_regular_rate', label: 'T&M Regular Rate', type: 'currency'},
            {key: 'tm_ot_rate', label: 'T&M OT Rate', type: 'currency'},
        ],
        tableProps: {
            columns: [
                {heading: 'Rate Type', accessor: 'rate_type_name'},
                {heading: 'Category', accessor: 'category'},
                {heading: 'Regular Rate', accessor: 'regular_rate', type: 'currency'},
                {heading: 'OT Rate', accessor: 'ot_rate', type: 'currency'},
                {heading: 'T&M Regular Rate', accessor: 'tm_regular_rate', type: 'currency'},
                {heading: 'T&M OT Rate', accessor: 'tm_ot_rate', type: 'currency'},
            ]
        }
    },

    {key: 'material_discounts',
        type: 'references',
        heading: 'Material Discounts',
        createLabel: 'Add Material Discount',
        createPrivileges: [Privileges.CREATE_CONTRACT_MATERIAL_DISCOUNTS],
        readPrivileges: [Privileges.READ_CONTRACT_MATERIAL_DISCOUNTS],
        deletePrivileges: [Privileges.DELETE_CONTRACT_MATERIAL_DISCOUNTS],
        initialLoadFromValues: true,
        dataUrl: '/contracts/:contract_number',
        dataTransform: 'material_discounts',
        createUrl: (values) => { return '/contract_material_discounts' },
        deleteUrl: (values) => { return '/contract_material_discounts/:id' },
        // Click a row to edit its discount. vendor is the row identity (contract_id +
        // vendor_id unique key) and changing it could collide with another row, so it's
        // shown read-only; to change vendor, delete the row and add a new one. Only
        // discount_pct is writable server-side (resources/contract_material_discounts.js).
        editUrl: '/contract_material_discounts/:id',
        editPrivileges: [Privileges.UPDATE_CONTRACT_MATERIAL_DISCOUNTS],
        editFields: [
            {key: 'vendor_name', label: 'Vendor', static: true, readOnly: true},
            {key: 'discount_pct', label: 'Discount %', type: 'percentage'},
        ],
        injectedValues: (values) => { return {contract_id: values.id} },
        formFields: [
            {key: 'vendor_id',
                displayKey: 'vendor_name',
                label: 'Vendor',
                type: 'autocomplete',
                suggestFormat: (v) => { return {value: v.id, text: v.name} },
                suggestUrl: '/vendors',
                suggestFields: ['id', 'name']},
            {key: 'discount_pct', label: 'Discount %', type: 'percentage'},
        ],
        tableProps: {
            columns: [
                {heading: 'Vendor', accessor: 'vendor_name'},
                {heading: 'Discount %', accessor: 'discount_pct', type: 'percentage'},
            ]
        }
    },

    // Full lineage: every term of this contract's lineage (REST returns them from contracts_extended).
    {key: 'lineage',
        type: 'references',
        heading: 'Contract Lineage',
        readPrivileges: [Privileges.SEARCH_CONTRACTS],
        dataUrl: '/contracts?base=:contract_number_base',
        tableProps: {
            rowURL: '/contracts/:contract_number',
            columns: [
                {heading: 'Term #', accessor: 'generation_seq'},
                {heading: 'Contract #', accessor: 'contract_number', style: 'bold'},
                {heading: 'Start', accessor: 'start_date', type: 'date', dateFormat: 'MMM DD, YYYY'},
                {heading: 'Expires', accessor: 'expires', type: 'date', dateFormat: 'MMM DD, YYYY'},
                {type: 'badge', heading: 'Status', accessor: 'status', badgePropsFunc: statusBadgeStyle},
            ]
        }
    },

    // Contract adjustments: the append-only contract_adjustments ledger (decision #75).
    // User-authored mid-term amount changes -- `amount` may be POSITIVE (increase) or NEGATIVE
    // (decrease), each with a description documenting the reason. The Generate Invoice action
    // amortizes from contract_amount + SUM(adjustments), so adding/editing/removing a row here
    // re-spreads the remaining balance over the remaining unbilled periods on the NEXT generate;
    // already-issued invoices are never regenerated (#73). Super-user only for now (decision #4),
    // gated on `superuser` (Privileges.ADMIN) exactly like the Billing Ledger below.
    {key: 'adjustments',
        type: 'references',
        heading: 'Contract Adjustments',
        createLabel: 'Add Adjustment',
        createPrivileges: [Privileges.ADMIN],
        readPrivileges: [Privileges.ADMIN],
        editPrivileges: [Privileges.ADMIN],
        deletePrivileges: [Privileges.ADMIN],
        // Fetch mode: LinkedEditor GETs dataUrl on mount and re-fetches after each create/edit/
        // delete. contract_adjustments?contract=:id maps to resources/contract_adjustments.js
        // (queryParam contract -> contract_id).
        dataUrl: '/contract_adjustments?contract=:id',
        createUrl: (values) => { return '/contract_adjustments' },
        editUrl: '/contract_adjustments/:id',
        deleteUrl: '/contract_adjustments/:id',
        deleteLabel: 'Delete Adjustments',
        injectedValues: (values) => { return {contract_id: values.id} },
        formFields: [
            // currency input accepts a leading minus for a negative (decrease) adjustment.
            {key: 'amount', label: 'Amount (+ increase / − decrease)', type: 'currency', required: true},
            {key: 'effective_date', label: 'Effective Date', type: 'date', required: true},
            {key: 'description', label: 'Description', type: 'longtext',
                placeholder: 'Reason for this adjustment'},
        ],
        editFields: [
            {key: 'amount', label: 'Amount (+ increase / − decrease)', type: 'currency'},
            {key: 'effective_date', label: 'Effective Date', type: 'date'},
            {key: 'description', label: 'Description', type: 'longtext'},
        ],
        tableProps: {
            columns: [
                {heading: 'Amount', accessor: 'amount', type: 'currency',
                    privileges: [Privileges.READ_CONTRACT_ADJUSTMENTS_AMOUNT]},
                {heading: 'Effective', accessor: 'effective_date', type: 'date', dateFormat: 'MMM DD, YYYY'},
                {heading: 'Description', accessor: 'description'},
                {heading: 'Added', accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY'},
            ]
        }
    },

    // Billing ledger: the append-only contract_invoices rows (decision #75), i.e. the
    // periods that have actually been invoiced/processed for this contract. Read-only (no
    // create/edit/delete URLs) -- entries are produced by the "Generate Invoice" action
    // below, never edited by hand. Fetch mode: LinkedEditor GETs the dataUrl on mount and
    // re-fetches on reload() after an invoice is generated.
    {key: 'billing_ledger',
        type: 'references',
        heading: 'Billing Ledger',
        // SUPER-USER ONLY for now (decision #4): billing is not yet wired to any non-super
        // role, so the ledger is gated on the `superuser` privilege (Privileges.ADMIN).
        // userHasPrivileges() only passes a non-super user who literally holds every listed
        // privilege -- requiring `superuser` means non-supers are denied outright while super
        // users pass via the superuser bypass. No role is meant to hold this yet; when billing
        // roles are defined, swap this back to a real grant (e.g. SEARCH_CONTRACT_INVOICES).
        readPrivileges: [Privileges.ADMIN],
        dataUrl: '/contract_invoices?contract=:id',
        // Per-row delete (super-user only, decision #4). Needed so a changed contract
        // start_date can take effect: the ledger MAX(period_end) watermark otherwise keeps
        // billing resuming from the old start. The backend guard (guardContractLedgerDelete)
        // refuses any row still linked to a LIVE invoice; rows with no invoice or only a voided
        // invoice delete cleanly. The linked (voided) invoice stays in /invoices for reporting.
        deletePrivileges: [Privileges.ADMIN],
        deleteLabel: 'Delete Ledger Rows',
        deleteUrl: '/contract_invoices/:id',
        tableProps: {
            // Each ledger row links through to its generated AR invoice's detail page, reusing
            // the app-wide invoice route (/invoices/:id -- see resources/projects.js invoices
            // grid, invoices.js, paymentRequests.js). The endpoint (contract_invoices_extended
            // via resources/contract_invoices.js) returns invoice_id + invoice_number. Rows with
            // no invoice yet (invoice_id NULL -- not-yet-generated periods, or after an invoice
            // is deleted/void-purged via ON DELETE SET NULL) are made non-clickable by
            // rowValidate so the row never navigates to /invoices/undefined.
            rowURL: '/invoices/:invoice_id',
            rowValidate: (record) => !!record.invoice_id,
            columns: [
                {heading: 'Period #', accessor: 'period_index',
                    // period_index is 0-based; display 1-based. Also avoids the Table text
                    // renderer treating 0 as falsy and rendering it as a blank placeholder.
                    valueFunc: (record) => record.period_index + 1},
                {heading: 'Period Start', accessor: 'period_start', type: 'date', dateFormat: 'MMM DD, YYYY'},
                {heading: 'Period End', accessor: 'period_end', type: 'date', dateFormat: 'MMM DD, YYYY'},
                {heading: 'Cycle', accessor: 'billing_cycle'},
                {heading: 'Amount', accessor: 'amount', type: 'currency',
                    privileges: [Privileges.READ_CONTRACT_INVOICES_AMOUNT]},
                // Human-facing invoice number from the joined invoices row. NULL when the period
                // has no invoice yet -> render an em dash placeholder. Voided invoices (soft-void
                // voided = TRUE) are flagged inline since invoice_voided rides along for free.
                {heading: 'Invoice #', accessor: 'invoice_number',
                    valueFunc: (record) => !record.invoice_number
                        ? '—'
                        : (record.invoice_voided ? `${record.invoice_number} (voided)` : record.invoice_number)},
                {heading: 'Generated', accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY'},
            ]
        }
    },
];

export default {
    defaults: {
        name: 'Contract',
        color: colors.contracts,
        formFields: form,
        actions: ['search', 'create', 'view'],
        // Backend keys reads/updates on contract_number, not id (see UI scope §7.4).
        identifierParam: 'contract_number',
    },

    create: {
        title: 'New Contract',
        // Creation must snapshot the contract_type defaults and seed the per-labor-type
        // rate rows + per-vendor material discount rows (procedures/contracts.js
        // createContract -> seedLaborRates / seedMaterialDiscounts). A plain resource POST
        // to /contracts inserts a bare contract row and does NOT seed those (see
        // est-back-end/resources/contracts.js header), which left UI-created contracts with
        // zero labor rates / material discounts. So we override the generic Create submit to
        // route through the createContract RPC (UI scope §S3 option A) instead of the plain
        // POST that the create-mode "Create" button would otherwise fire.
        //
        // `values` are the form's normalized values (components/Form normalizeValues), which
        // already carry contract_type + the numeric fields. The /rpc endpoint replies with a
        // JSON-RPC envelope {jsonrpc, result, id}; unwrap `.result` and re-shape into the
        // {status, body, errors} contract that components/Create.handleSubmit expects so its
        // redirect (urlFormatter -> /contracts/:contract_number) works unchanged.
        createMethod: async (values) => {
            const response = await api.rpc('createContract', values);
            const envelope = (response && response.body) || {};
            if (!response || !response.success || envelope.error) {
                const message = (envelope.error && envelope.error.message)
                    || 'The contract could not be created.';
                return { status: response ? response.status : 500, errors: [{ message }] };
            }
            return { status: response.status, body: envelope.result };
        },
    },

    view: {
        title: (c) => c.contract_number || 'Contract',
        // After a top-level contract delete, land on the Contracts list rather than
        // history.goBack(). goBack() intermittently returned to the just-deleted
        // contract's own URL (renewal successors are reached via the Renew push /
        // edit-save redirect) -> 404 "requested resource was not found". The list
        // always resolves; the predecessor is one click away.
        deleteRedirect: () => '/contracts',
        // Same escape hatch for a record deleted out from under a back-navigation: after
        // cancelling a contract soft-deletes its renewal draft, 'Previous'/browser Back can
        // return to the deleted draft's detail route. withResource replace()s here on a 404
        // instead of stranding the user on "The requested resource was not found."
        notFoundRedirect: () => '/contracts',
        remarks: {
            header: (values) => values.contract_number,
            object_type: 'contract',
            object_id_key: 'id',
        },
        formActions: [
            {   label: 'Renew Contract',
                icon: 'reload',
                privileges: [Privileges.RENEW_CONTRACT],
                validate: ({values}) => {
                    return (values.status === 'active' || values.status === 'expired') && !values.is_renewed;
                },
                method: async ({values, history, loading, success, error}) => {
                    loading('Renewing contract');
                    let result;
                    try {
                        result = await api.rpc('renewContract', {contract_id: values.id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    // /rpc replies with a JSON-RPC envelope {jsonrpc, result, id}; the
                    // successor contract is under `.result`, not the envelope root. Reading
                    // result.body.contract_number directly yielded undefined and navigated to
                    // /contracts/undefined -> 404 ("requested resource was not found") even
                    // though the draft was committed server-side. Unwrap `.result` like the
                    // createContract handler above.
                    const envelope = (result && result.body) || {};
                    if (result && result.success && envelope.result && !envelope.error) {
                        history.push(`/contracts/${envelope.result.contract_number}`);
                    } else {
                        const message = (envelope.error && envelope.error.message)
                            || 'The contract could not be renewed.';
                        error({title: 'Error', content: message, okText: 'Ok'});
                    }
                }
            },
            {   label: 'Renew (Skip Increase)',
                icon: 'reload',
                privileges: [Privileges.RENEW_CONTRACT, Privileges.SKIP_CONTRACT_INCREASE],
                validate: ({values}) => {
                    return (values.status === 'active' || values.status === 'expired') && !values.is_renewed;
                },
                method: async ({values, history, loading, success, error}) => {
                    loading('Renewing contract');
                    let result;
                    try {
                        result = await api.rpc('renewContract', {contract_id: values.id, skip_increase: true});
                    } catch (e) {
                        throw new Error(e);
                    }

                    // See the "Renew Contract" handler above: unwrap the JSON-RPC `.result`
                    // envelope so we navigate to the real contract_number, not undefined.
                    const envelope = (result && result.body) || {};
                    if (result && result.success && envelope.result && !envelope.error) {
                        history.push(`/contracts/${envelope.result.contract_number}`);
                    } else {
                        const message = (envelope.error && envelope.error.message)
                            || 'The contract could not be renewed.';
                        error({title: 'Error', content: message, okText: 'Ok'});
                    }
                }
            },
            {   label: 'Send Renewal Notice',
                // Manual-first renewal notice (mirrors the invoice-email flow): send the notice
                // FROM this draft successor to the bill-to customer's contract email (falling back
                // statement -> invoice). One-send lock: sendContractRenewalNotice claims
                // renewal_notice_sent_at atomically, so this button hides once sent (validate
                // checks !renewal_notice_sent_at, and reload() re-fetches after a send). Shown
                // only on renewal successors (is_renewal) that haven't been sent yet.
                // Superuser-only for now (RPC_sendContractRenewalNotice granted to 0 roles).
                icon: 'mail',
                privileges: [Privileges.ADMIN],
                validate: ({values}) => {
                    return values.is_renewal === true && !values.renewal_notice_sent_at;
                },
                method: async ({values, reload, loading, success, error}) => {
                    loading('Sending renewal notice');
                    let result;
                    try {
                        result = await api.rpc('sendContractRenewalNotice', {contract_id: values.id});
                    } catch (e) {
                        throw new Error(e);
                    }
                    const envelope = (result && result.body) || {};
                    if (result && result.success && envelope.result && !envelope.error) {
                        const r = envelope.result;
                        success({title: 'Renewal Notice Sent',
                                 content: `The renewal notice for ${r.contract_number} was sent to ${r.to}`
                                    + `${r.cc && r.cc.length ? ` (cc ${r.cc.join(', ')})` : ''}.`,
                                 okText: 'Ok'});
                        reload();
                    } else {
                        const message = (envelope.error && envelope.error.message)
                            || 'The renewal notice could not be sent.';
                        error({title: 'Error', content: message, okText: 'Ok'});
                    }
                }
            },
            {   label: 'Turn Off Auto-Renew',
                icon: 'stop',
                // Mark the active contract as no-longer-auto-renewing (decision #4: an alternative
                // to deleting a draft / setting a cancel date). Plain PATCH of auto_renew=false.
                // Shown on a live (not-yet-renewed, not-cancelled) contract that still auto-renews.
                privileges: [Privileges.UPDATE_CONTRACTS],
                validate: ({values}) => {
                    return values.auto_renew === true
                        && !values.is_renewed
                        && values.status !== 'cancelled';
                },
                method: async ({values, reload, loading, success, error}) => {
                    loading('Turning off auto-renew');
                    let result;
                    try {
                        result = await api.update(`/contracts/${values.contract_number}`, {auto_renew: false});
                    } catch (e) {
                        throw new Error(e);
                    }
                    if (result && result.success) {
                        success({title: 'Auto-Renew Off',
                                 content: 'This contract will no longer renew automatically.',
                                 okText: 'Ok'});
                        reload();
                    } else {
                        error({title: 'Error', content: 'Auto-renew could not be turned off.', okText: 'Ok'});
                    }
                }
            },
            {   label: 'Generate Invoice',
                icon: 'file-text',
                // One-off manual billing: records the OLDEST unbilled period in the
                // contract_invoices ledger (procedures/contracts.js generateContractInvoice),
                // billing a prorated slice of the remaining contract balance (decision #1).
                // The scheduled/automated billing engine is on hold (NQ-14); this is the
                // manual gate. Does not create a public invoices row or send anything yet.
                //
                // SUPER-USER ONLY for now (decision #4): gated on the `superuser` privilege
                // (Privileges.ADMIN), not GENERATE_CONTRACT_INVOICE, because billing is not yet
                // assigned to any non-super role. userHasPrivileges(['superuser']) denies every
                // non-super user and passes super users via the bypass. Swap back to
                // GENERATE_CONTRACT_INVOICE once a billing role is granted that privilege.
                privileges: [Privileges.ADMIN],
                validate: ({values}) => {
                    // 'draft' allowed so a future-dated contract can have its first
                    // invoice generated manually before start_date. Backend has no
                    // status gate; contract_billing_periods() anchors on start_date, so
                    // the first billable period is the (future) start_date period and the
                    // invoice is dated to that period_start. 'cancelled' stays excluded.
                    return values.status === 'active'
                        || values.status === 'expired'
                        || values.status === 'draft';
                },
                method: async ({values, reload, loading, success, error}) => {
                    loading('Generating invoice');
                    let result;
                    try {
                        result = await api.rpc('generateContractInvoice', {contract_id: values.id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    // /rpc replies with a JSON-RPC envelope {jsonrpc, result, id}; unwrap
                    // `.result` like the renew/create handlers.
                    const envelope = (result && result.body) || {};
                    if (result && result.success && envelope.result && !envelope.error) {
                        const r = envelope.result;
                        let content;
                        if (!r.generated) {
                            content = r.message || 'Nothing to invoice.';
                        } else if (r.grouped) {
                            // Grouped path returns no top-level entry/proration —
                            // it returns `lines` (one per contract). Summarize them.
                            const lines = r.lines || [];
                            const total = r.invoice_total != null
                                ? ` for $${Number(r.invoice_total).toFixed(2)}`
                                : '';
                            const detail = lines.map(l =>
                                `${l.contract_number}: ${l.entry.period_start} – ${l.entry.period_end}`
                                + ` ($${Number(l.invoice_amount).toFixed(2)})`).join('\n');
                            content = `Combined invoice for ${lines.length} contract`
                                + `${lines.length === 1 ? '' : 's'}${total}.\n${detail}`;
                        } else {
                            const amt = r.proration && r.proration.invoice_amount != null
                                ? ` for $${Number(r.proration.invoice_amount).toFixed(2)}`
                                : '';
                            content = `Invoiced period ${r.entry.period_start} – ${r.entry.period_end}${amt}.`;
                        }
                        success({title: r.generated ? 'Invoice Generated' : 'No Invoice',
                                 content, okText: 'Ok'});
                        reload();
                    } else {
                        const message = (envelope.error && envelope.error.message)
                            || 'The invoice could not be generated.';
                        error({title: 'Error', content: message, okText: 'Ok'});
                    }
                }
            },
            {   label: 'Cancel Contract',
                icon: 'stop',
                submitText: 'Cancel Contract',
                // No cancel RPC exists — cancelling is PATCH { dead_date } (UI scope §S4-actions/§7).
                // The user picks the cancellation date; an optional note is captured as a
                // contract remark (object_type 'contract', object_id = contract id).
                privileges: [Privileges.CANCEL_CONTRACT, Privileges.UPDATE_CONTRACTS],
                validate: ({values}) => {
                    return values.status !== 'cancelled';
                },
                parametersForm: [
                    {key: 'dead_date', label: 'Cancellation Date', type: 'date', required: true},
                    {key: 'note', label: 'Cancellation Note', type: 'longtext',
                     placeholder: 'Reason for cancellation (added to the contract remarks)'},
                ],
                method: async ({values, parameters, reload, loading, success, error}) => {
                    const { dead_date, note } = parameters;
                    loading('Cancelling contract');
                    let result;
                    try {
                        result = await api.update(`/contracts/${values.contract_number}`, {dead_date});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (!result.success) {
                        error({title: 'Error', content: 'The contract could not be cancelled.', okText: 'Ok'});
                        return;
                    }

                    // Capture the cancellation note as a contract remark. Best-effort: the
                    // cancellation itself already succeeded, so a remark failure must not
                    // surface as a cancellation error.
                    const text = (note || '').trim();
                    if (text) {
                        try {
                            const whoami = await api.verify();
                            const user_id = whoami && whoami.body && whoami.body.id;
                            await api.create('/remarks', {
                                header: values.contract_number,
                                user_id,
                                object_id: values.id,
                                object_type: 'contract',
                                type: 'comment',
                                body: `Cancelled effective ${String(dead_date).slice(0, 10)}: ${text}`,
                                autogenerated: false,
                            });
                        } catch (e) {
                            // non-critical — leave the cancellation reported as successful
                        }
                    }

                    success({title: 'Cancelled', content: 'The contract has been cancelled.', okText: 'Ok'});
                    reload();
                }
            },
        ],
    },

    search: {
        fields: ['contract_number', 'contract_number_base', 'site_name', 'bill_to_customer_name',
                 'contract_type', 'contract_amount', 'start_date', 'expires', 'dead_date', 'auto_renew',
                 'status', 'created_at'],
        filterOptions: [
            {label: 'Active', filters: {status: 'active'}, default: true},
            {label: 'Cancelled', filters: {status: 'cancelled'}},
            {label: 'Draft', filters: {status: 'draft'}},
            {label: 'All', filters: null},
        ],
        links: ['new',
            {url: '/contracts/invoice_comparison', title: 'Invoice Comparison Report', icon: 'file-pdf',
             requiredPrivileges: [Privileges.FILE_READ_CONTRACT_INVOICE_COMPARISON]},
        ],
        searchBy: ['contract number', 'site', 'customer'],
        tableProps: {
            rowURL: '/contracts/:contract_number',
            columns: [
                {heading: 'Contract #', accessor: 'contract_number', style: 'bold', cellWidth: '120px'},
                {heading: 'Site', accessor: 'site_name', placeholder: '(No Site)'},
                {heading: 'Bill-To', accessor: 'bill_to_customer_name', placeholder: '(No Customer)'},
                {heading: 'Type', accessor: 'contract_type'},
                {heading: 'Start', accessor: 'start_date', type: 'date', dateFormat: 'MMM DD, YYYY'},
                {heading: 'Expires', accessor: 'expires', type: 'date', dateFormat: 'MMM DD, YYYY',
                    valueFunc: (value, row) => row.dead_date || value},
                // Auto Renews: the stored auto_renew flag, but forced to No when a cancellation
                // date lands before the current term's renewal point (expires) — the contract
                // dies before it would ever renew.
                {heading: 'Auto Renews', accessor: 'auto_renew', type: 'text',
                    valueFunc: (row) => {
                        const cancelledBeforeRenewal = row.dead_date &&
                            new Date(row.dead_date) < new Date(row.expires);
                        return (row.auto_renew && !cancelledBeforeRenewal) ? 'Yes' : 'No';
                    }},
                {heading: 'Amount', accessor: 'contract_amount', type: 'currency',
                    privileges: [Privileges.READ_CONTRACTS_CONTRACT_AMOUNT]},
                {type: 'badge', heading: 'Status', accessor: 'status', badgePropsFunc: statusBadgeStyle},
                {heading: 'Created', accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY'},
            ]
        }
    },
};
